import PropTypes from 'prop-types';
import styled from 'styled-components';
import { colors } from 'themes';

const Wrapper = styled.article`
  margin: 40px 0;
`;

const DraftMessage = styled.p`
  color: ${({ $isDraft }) => ($isDraft ? colors.warning : colors.black)};
`;

/**
 * Static instructional copy explaining how to embed a widget's HTML snippet
 * and customize it via the `data-width`, `data-height`/`data-height-full`, and
 * `data-css` attributes. Appends a warning when the entity being embedded is
 * still a draft. Use alongside `WidgetCard` on widget embed pages.
 */
const WidgetInstructions = ({ widgetType, isDraft }) => {
  // eslint-disable-next-line react/destructuring-assignment
  const article = widgetType[0] !== 'a' ? 'a' : 'an';

  const DRAFT_FORM_MESSAGE = `Widget forms will show an error if embedded before the ${widgetType} is published.`;

  return (
    <Wrapper>
      <h3>Instructions</h3>
      <p>
        The widgets allow you to embed {article} {widgetType} directly in your own website. Click on
        the widget preview to see the HTML snippet, or just click the &#34;copy&#34; button to copy
        the code. If you have access to the website where you will be embedding the code you paste
        this into the page exactly where you want the form. Otherwise you can share the code with
        your developer or web content manager.
      </p>
      <p>
        The embedding code exposes several ways that you can control the layout of the widget in
        your site, and also the way the form itself is styled.
      </p>
      <ul>
        <li>
          The <strong>width</strong> of the widget can be controlled using the{' '}
          <code>data-width</code> attribute of the <code>&#60;div&#62;</code> element.
        </li>
        <li>
          The <strong>height</strong> of the widget can be controlled by setting the{' '}
          <code>data-height</code> attribute of the <code>&#60;div&#62;</code> element. You can
          achieve full page height without scrolling by setting the following attribute:{' '}
          <code>data-height-full=&quot;true&quot;</code>
          {'. '}
        </li>
        <li>
          You can <strong>style</strong> your embedded widget using an external CSS stylesheet
          referenced in the <code>data-css</code> attribute of the <code>&#60;div&#62;</code>{' '}
          element. The value should be a URL to the CSS file.
        </li>
      </ul>
      <DraftMessage $isDraft={isDraft}>{isDraft ? `${DRAFT_FORM_MESSAGE}` : ''}</DraftMessage>
    </Wrapper>
  );
};

WidgetInstructions.propTypes = {
  isDraft: PropTypes.bool.isRequired,
  /** Entity noun interpolated into the copy, e.g. "action center" or "signup". */
  widgetType: PropTypes.string.isRequired,
};

export default WidgetInstructions;
